import { useCallback, useState } from 'react';

type UseStateHistory<T> = [
  current: T,
  setNext: (value: T) => void,
  setBack: () => void,
  hasChanged: boolean,
];

const MAX_HISTORY_SIZE = 20;

export const useStateHistory = <T>(initialValue: T): UseStateHistory<T> => {
  const [history, setHistory] = useState<T[]>([initialValue]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const setNext = useCallback(
    (value: T) => {
      setHistory((prev) => {
        // Remove any future history if we're not at the end
        const newHistory = prev.slice(0, currentIndex + 1);
        const updatedHistory = [...newHistory, value];

        // If exceeding max size, remove oldest entries
        if (updatedHistory.length > MAX_HISTORY_SIZE) {
          return updatedHistory.slice(updatedHistory.length - MAX_HISTORY_SIZE);
        }

        return updatedHistory;
      });
      setCurrentIndex((prev) => {
        const newIndex = prev + 1;
        // Adjust index if we're at max capacity
        return newIndex >= MAX_HISTORY_SIZE ? MAX_HISTORY_SIZE - 1 : newIndex;
      });
    },
    [currentIndex]
  );

  const setBack = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex]);

  const current = history[currentIndex] ?? initialValue;

  const hasChanged = history.length > 1;

  return [current, setNext, setBack, hasChanged];
};
